import React, { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Button,
  Chip,
  Drawer,
  IconButton,
  Paper,
  Skeleton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import PriceChangeOutlinedIcon from "@mui/icons-material/PriceChangeOutlined";
import { formatServiceLevelSummary, getServiceLabel, getServiceLevelLabel } from "../../../../libs/labels";

const DEFAULT_MONTHLY_HOURS = (40 * 52) / 12;

const formatDate = (ts) => {
  const sec = Number(ts?.seconds || ts?._seconds || 0);
  if (sec > 0) return new Date(sec * 1000).toLocaleDateString();
  if (typeof ts?.toDate === "function") {
    const dt = ts.toDate();
    if (dt instanceof Date && !Number.isNaN(dt.getTime())) return dt.toLocaleDateString();
  }
  return "—";
};

const formatAnyDate = (value) => {
  if (typeof value === "string" && value.trim()) {
    const parsed = Date.parse(value);
    return Number.isNaN(parsed) ? value : new Date(parsed).toLocaleDateString();
  }
  return formatDate(value);
};

const getFirstDayOfNextMonthString = () => {
  const now = new Date();
  const dt = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() + 1, 1));
  const y = dt.getUTCFullYear();
  const m = String(dt.getUTCMonth() + 1).padStart(2, "0");
  const d = String(dt.getUTCDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const isFirstDayOfMonth = (value) => String(value || "").trim().endsWith("-01");

const normalizeToMonthStart = (value) => {
  const raw = String(value || "").trim();
  const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (!match) return "";
  return `${match[1]}-${match[2]}-01`;
};

const toTimestampMs = (value) => {
  if (typeof value === "string" && value.trim()) {
    const parsed = Date.parse(value);
    if (!Number.isNaN(parsed)) return parsed;
  }
  if (typeof value?.seconds === "number") return value.seconds * 1000;
  if (typeof value?.toDate === "function") {
    const dt = value.toDate();
    if (dt instanceof Date && !Number.isNaN(dt.getTime())) return dt.getTime();
  }
  return 0;
};

const getClientHourlyCents = (contract) => {
  const model = String(contract?.pricingModel || "").toLowerCase();
  if (model === "fixed_monthly") {
    const monthly = Number(contract?.monthlyBudgetCents || 0);
    return monthly > 0 ? Math.round(monthly / DEFAULT_MONTHLY_HOURS) : 0;
  }
  return Number(contract?.hourlyBudgetCents || 0);
};

const getContractorHourlyCentsFromContract = (contract) => {
  return Number(
    contract?.pricingSnapshot?.contractor?.hourlyCents ||
      contract?.contractor?.hourlyCents ||
      0
  );
};

const formatStatus = (value) =>
  String(value || "unknown")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (ch) => ch.toUpperCase());

const isEmailLike = (value) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(value || "").trim());

const PricingUpdates = ({
  loading,
  pricingRequests = [],
  contracts = [],
  busyById,
  resolvePricing,
  formatCurrency,
  resolveUserLabel,
  resolveContractorFullName,
}) => {
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(5);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedRequestId, setSelectedRequestId] = useState("");
  const [approvalEffectiveDate, setApprovalEffectiveDate] = useState("");
  const [approvalError, setApprovalError] = useState("");
  const nextMonthMinDate = useMemo(() => getFirstDayOfNextMonthString(), []);

  const rows = useMemo(
    () => pricingRequests.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
    [pricingRequests, page, rowsPerPage]
  );

  const selectedRequest = useMemo(
    () => pricingRequests.find((row) => String(row.id || "") === String(selectedRequestId || "")) || null,
    [pricingRequests, selectedRequestId]
  );

  const impactedContracts = useMemo(() => {
    if (!selectedRequest?.uid) return [];
    return contracts
      .filter((contract) => {
        const status = String(contract?.status || "").toLowerCase();
        const contractorUid = String(contract?.contractorUid || contract?.matchedContractorUid || "").trim();
        return ["active", "signed", "matched"].includes(status) && contractorUid === String(selectedRequest.uid);
      })
      .sort((a, b) => {
        const aMs = toTimestampMs(a?.updatedAt || a?.createdAt);
        const bMs = toTimestampMs(b?.updatedAt || b?.createdAt);
        return bMs - aMs;
      });
  }, [contracts, selectedRequest]);

  const getClientContractLabel = (contract = {}) => {
    const preferred = [
      contract?.companyName,
      contract?.accountName,
    ]
      .map((v) => String(v || "").trim())
      .find((v) => v && !isEmailLike(v));
    if (preferred) return preferred;
    return "Client";
  };

  const profitability = useMemo(() => {
    if (!selectedRequest) return null;
    const requestedHourlyCents = Number(selectedRequest.requestedHourlyCents || 0);
    const fallbackCurrentHourlyCents = Number(selectedRequest.currentHourlyCents || 0);

    const rowsWithImpact = impactedContracts.map((contract) => {
      const clientHourlyCents = getClientHourlyCents(contract);
      const currentHourlyFromContract = getContractorHourlyCentsFromContract(contract);
      const currentHourlyCents = currentHourlyFromContract > 0 ? currentHourlyFromContract : fallbackCurrentHourlyCents;
      const currentMarginHourlyCents = clientHourlyCents - currentHourlyCents;
      const requestedMarginHourlyCents = clientHourlyCents - requestedHourlyCents;
      return {
        contract,
        clientHourlyCents,
        currentHourlyCents,
        requestedHourlyCents,
        currentMarginHourlyCents,
        requestedMarginHourlyCents,
        currentMonthlyMarginCents: Math.round(currentMarginHourlyCents * DEFAULT_MONTHLY_HOURS),
        requestedMonthlyMarginCents: Math.round(requestedMarginHourlyCents * DEFAULT_MONTHLY_HOURS),
      };
    });

    const totals = rowsWithImpact.reduce(
      (acc, row) => {
        acc.currentMonthlyMarginCents += row.currentMonthlyMarginCents;
        acc.requestedMonthlyMarginCents += row.requestedMonthlyMarginCents;
        return acc;
      },
      { currentMonthlyMarginCents: 0, requestedMonthlyMarginCents: 0 }
    );

    return {
      rows: rowsWithImpact,
      currentHourlyCents: fallbackCurrentHourlyCents,
      requestedHourlyCents,
      hourlyDeltaCents: requestedHourlyCents - fallbackCurrentHourlyCents,
      currentMonthlyMarginCents: totals.currentMonthlyMarginCents,
      requestedMonthlyMarginCents: totals.requestedMonthlyMarginCents,
      monthlyDeltaCents: totals.requestedMonthlyMarginCents - totals.currentMonthlyMarginCents,
    };
  }, [selectedRequest, impactedContracts]);

  const openDetails = (requestId) => {
    const request = pricingRequests.find((row) => String(row.id || "") === String(requestId || ""));
    const normalizedExisting = normalizeToMonthStart(String(request?.effectiveDate || "").trim());
    setSelectedRequestId(String(requestId || ""));
    setApprovalError("");
    setApprovalEffectiveDate(normalizedExisting || nextMonthMinDate);
    setDetailsOpen(true);
  };

  const submitApproval = async () => {
    if (!selectedRequest) return;
    if (String(selectedRequest.status || "").toLowerCase() !== "pending") {
      setApprovalError("Only pending requests can be approved.");
      return;
    }
    if (!approvalEffectiveDate) {
      setApprovalError("Effective date is required.");
      return;
    }
    if (approvalEffectiveDate < nextMonthMinDate) {
      setApprovalError(`Effective date must be ${nextMonthMinDate} or later.`);
      return;
    }
    if (!isFirstDayOfMonth(approvalEffectiveDate)) {
      setApprovalError("Effective date must be the first day of a month.");
      return;
    }
    setApprovalError("");
    await resolvePricing(selectedRequest, "approved", { effectiveDate: approvalEffectiveDate });
    setDetailsOpen(false);
  };

  return (
    <>
      <Paper variant="outlined" sx={{ p: 2, borderRadius: "8px", mb: 2, display: "flex", flexDirection: "column" }}>
        <Stack direction="row" spacing={1.25} alignItems="center" sx={{ mb: 1 }}>
          <Avatar variant="rounded" sx={{ width: 28, height: 28, bgcolor: "grey.200", color: "grey.700" }}>
            <PriceChangeOutlinedIcon fontSize="small" />
          </Avatar>
          <Typography sx={{ fontWeight: 600 }}>Pricing Updates</Typography>
        </Stack>

        {loading ? (
          <Stack spacing={1} sx={{ flexGrow: 1 }}>
            {Array.from({ length: 5 }).map((_, idx) => (
              <Skeleton key={idx} variant="rounded" height={40} />
            ))}
          </Stack>
        ) : pricingRequests.length === 0 ? (
          <Box sx={{ flexGrow: 1, minHeight: 140, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <Typography variant="body2" color="text.secondary">No pricing requests to review right now.</Typography>
          </Box>
        ) : (
          <TableContainer sx={{ flexGrow: 1 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Date</TableCell>
                  <TableCell>Contractor</TableCell>
                  <TableCell>Service</TableCell>
                  <TableCell>Level</TableCell>
                  <TableCell align="right">Rate</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((req) => (
                  <TableRow key={req.id} hover sx={{ cursor: "pointer" }} onClick={() => openDetails(req.id)}>
                    <TableCell>{formatDate(req.updatedAt || req.createdAt)}</TableCell>
                    <TableCell>{resolveContractorFullName?.(req.uid) || "Contractor"}</TableCell>
                    <TableCell>{getServiceLabel(req.requestedService || req.service)}</TableCell>
                    <TableCell>{getServiceLevelLabel(req.requestedService || req.service, req.requestedLevel || req.level)}</TableCell>
                    <TableCell align="right">{formatCurrency(req.requestedHourlyCents || 0, req.requestedCurrency || req.currency || "usd")}/hr</TableCell>
                  </TableRow>
                ))}
              </TableBody>
              <TableFooter>
                <TableRow>
                  <TablePagination
                    count={pricingRequests.length}
                    page={page}
                    onPageChange={(_event, newPage) => setPage(newPage)}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={(e) => {
                      setRowsPerPage(parseInt(e.target.value, 10));
                      setPage(0);
                    }}
                    rowsPerPageOptions={[5, 10, 20]}
                    labelRowsPerPage="Show"
                    colSpan={5}
                  />
                </TableRow>
              </TableFooter>
            </Table>
          </TableContainer>
        )}
      </Paper>

      <Drawer
        anchor="bottom"
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        PaperProps={{
          sx: {
            top: { xs: 56, sm: 64 },
            height: { xs: "calc(100vh - 56px)", sm: "calc(100vh - 64px)" },
            borderTopLeftRadius: 14,
            borderTopRightRadius: 14,
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <Box
          sx={{
            p: { xs: 2, md: 2.5 },
            borderBottom: (t) => `1px solid ${t.palette.divider}`,
            position: "sticky",
            top: 0,
            zIndex: 2,
            bgcolor: "background.paper",
          }}
        >
          <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={1}>
            <Box>
              <Typography variant="h6" sx={{ fontWeight: 700 }}>Pricing Request Details</Typography>
              <Typography variant="body2" color="text.secondary">
                Review active-contract impact before approval.
              </Typography>
            </Box>
            <IconButton size="small" onClick={() => setDetailsOpen(false)} aria-label="Close pricing request drawer">
              <CloseIcon fontSize="small" />
            </IconButton>
          </Stack>
        </Box>

        <Box sx={{ p: { xs: 2, md: 3 }, overflowY: "auto", flex: 1, bgcolor: "grey.50" }}>
          {!selectedRequest ? (
            <Typography variant="body2" color="text.secondary">Request not found.</Typography>
          ) : (
            <Stack spacing={2.25}>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>Contractor</Typography>
                <Typography variant="body2" color="text.secondary">
                  {resolveContractorFullName?.(selectedRequest.uid) || "Contractor"}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {String(selectedRequest.email || "—")}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {formatServiceLevelSummary(selectedRequest.requestedService || selectedRequest.service, selectedRequest.requestedLevel || selectedRequest.level)}
                </Typography>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>Rate Comparison</Typography>
                <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
                  <Chip label={`Current: ${formatCurrency(profitability?.currentHourlyCents || 0, selectedRequest.requestedCurrency || selectedRequest.currency || "usd")}/hr`} />
                  <Chip label={`Requested: ${formatCurrency(profitability?.requestedHourlyCents || 0, selectedRequest.requestedCurrency || selectedRequest.currency || "usd")}/hr`} />
                  <Chip
                    color={Number(profitability?.hourlyDeltaCents || 0) > 0 ? "warning" : "default"}
                    label={`Delta: ${Number(profitability?.hourlyDeltaCents || 0) >= 0 ? "+" : ""}${formatCurrency(profitability?.hourlyDeltaCents || 0, selectedRequest.requestedCurrency || selectedRequest.currency || "usd")}/hr`}
                  />
                </Stack>
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>Active-Contract Impact</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.25 }}>
                  Contracts in status Active, Signed, or Matched for this contractor.
                </Typography>

                {impactedContracts.length === 0 ? (
                  <Typography variant="body2" color="text.secondary">No impacted contracts found.</Typography>
                ) : (
                  <>
                    <Stack direction={{ xs: "column", sm: "row" }} spacing={1} sx={{ mb: 1.5 }}>
                      <Chip
                        color={Number(profitability?.currentMonthlyMarginCents || 0) >= 0 ? "success" : "error"}
                        label={`Current Monthly Margin: ${formatCurrency(profitability?.currentMonthlyMarginCents || 0, selectedRequest.requestedCurrency || selectedRequest.currency || "usd")}`}
                      />
                      <Chip
                        color={Number(profitability?.requestedMonthlyMarginCents || 0) >= 0 ? "success" : "error"}
                        label={`Requested Monthly Margin: ${formatCurrency(profitability?.requestedMonthlyMarginCents || 0, selectedRequest.requestedCurrency || selectedRequest.currency || "usd")}`}
                      />
                      <Chip
                        color={Number(profitability?.monthlyDeltaCents || 0) <= 0 ? "success" : "warning"}
                        label={`Delta: ${Number(profitability?.monthlyDeltaCents || 0) >= 0 ? "+" : ""}${formatCurrency(profitability?.monthlyDeltaCents || 0, selectedRequest.requestedCurrency || selectedRequest.currency || "usd")}`}
                      />
                    </Stack>

                    <TableContainer>
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell>Contract</TableCell>
                            <TableCell>Status</TableCell>
                            <TableCell align="right">Client/hr</TableCell>
                            <TableCell align="right">Current Margin/hr</TableCell>
                            <TableCell align="right">Requested Margin/hr</TableCell>
                            <TableCell>End Date</TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {profitability?.rows?.map((row) => {
                            const contractId = String(row.contract?.id || "");
                            const currentNegative = Number(row.currentMarginHourlyCents || 0) < 0;
                            const requestedNegative = Number(row.requestedMarginHourlyCents || 0) < 0;
                            return (
                              <TableRow key={contractId || `${row.contract?.updatedAt?.seconds || 0}`}>
                                <TableCell sx={{ maxWidth: 180 }}>
                                  <Tooltip title={contractId || "—"}>
                                    <Typography variant="body2" sx={{ fontWeight: 600 }} noWrap>
                                      {getClientContractLabel(row.contract)}
                                    </Typography>
                                  </Tooltip>
                                  <Typography variant="caption" color="text.secondary" noWrap>
                                    {contractId || "—"}
                                  </Typography>
                                </TableCell>
                                <TableCell>{formatStatus(row.contract?.status)}</TableCell>
                                <TableCell align="right">
                                  {formatCurrency(row.clientHourlyCents, row.contract?.currency || selectedRequest.requestedCurrency || selectedRequest.currency || "usd")}
                                </TableCell>
                                <TableCell align="right" sx={{ color: currentNegative ? "error.main" : "success.main" }}>
                                  {formatCurrency(row.currentMarginHourlyCents, row.contract?.currency || selectedRequest.requestedCurrency || selectedRequest.currency || "usd")}
                                </TableCell>
                                <TableCell align="right" sx={{ color: requestedNegative ? "error.main" : "success.main" }}>
                                  {formatCurrency(row.requestedMarginHourlyCents, row.contract?.currency || selectedRequest.requestedCurrency || selectedRequest.currency || "usd")}
                                </TableCell>
                                <TableCell>{formatAnyDate(row.contract?.contractEndAt)}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                )}
              </Paper>

              <Paper variant="outlined" sx={{ p: 2, borderRadius: 1.5 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.25 }}>Request Status</Typography>
                <Stack direction={{ xs: "column", md: "row" }} spacing={2} alignItems={{ xs: "stretch", md: "flex-start" }}>
                  <Box sx={{ minWidth: { md: 220 }, maxWidth: { md: 280 } }}>
                    <Chip sx={{ mt: 1 }} size="large" label={formatStatus(selectedRequest.status || "unknown")} />
                    {String(selectedRequest.status || "").toLowerCase() !== "pending" && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        This request is already resolved. Approval actions are disabled.
                      </Typography>
                    )}
                  </Box>

                  <Stack spacing={1} sx={{ flex: 1, minWidth: 0, alignItems: { xs: "stretch", md: "flex-end" } }}>
                    <TextField
                      type="date"
                      size="small"
                      label="Effective Date"
                      value={approvalEffectiveDate}
                      onChange={(e) => {
                        const value = normalizeToMonthStart(e.target.value) || e.target.value;
                        setApprovalEffectiveDate(value);
                        if (!value) {
                          setApprovalError("Effective date is required.");
                        } else if (value < nextMonthMinDate) {
                          setApprovalError(`Effective date must be ${nextMonthMinDate} or later.`);
                        } else if (!isFirstDayOfMonth(value)) {
                          setApprovalError("Effective date must be the first day of a month.");
                        } else {
                          setApprovalError("");
                        }
                      }}
                      InputLabelProps={{ shrink: true }}
                      sx={{ minWidth: 220, width: { xs: "100%", md: 320 } }}
                      inputProps={{ min: nextMonthMinDate }}
                      error={!!approvalError}
                      helperText={approvalError || `Select first day of month, starting ${nextMonthMinDate}.`}
                    />
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        variant="contained"
                        size="small"
                        sx={{ minHeight: 40, minWidth: 110 }}
                        disabled={!!busyById[selectedRequest.id] || String(selectedRequest.status || "").toLowerCase() !== "pending"}
                        onClick={submitApproval}
                      >
                        Approve
                      </Button>
                      <Button
                        variant="outlined"
                        size="small"
                        color="error"
                        sx={{ minHeight: 40, minWidth: 110 }}
                        disabled={!!busyById[selectedRequest.id] || String(selectedRequest.status || "").toLowerCase() !== "pending"}
                        onClick={async () => {
                          await resolvePricing(selectedRequest, "rejected");
                          setDetailsOpen(false);
                        }}
                      >
                        Reject
                      </Button>
                    </Stack>
                  </Stack>
                </Stack>
              </Paper>
            </Stack>
          )}
        </Box>
      </Drawer>
    </>
  );
};

export default PricingUpdates;
